import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../../../firebase";

import OnboardingMeStep from "./components/OnboardingMeStep";
import OnboardingEligibilityStep from "./components/OnboardingEligibilityStep";
import OnboardingSpouseStep from "./components/OnboardingSpouseStep";
import OnboardingRelationshipStatusStep from "./components/OnboardingRelationshipStatusStep";
import OnboardingRelationshipDatesStep from "./components/OnboardingRelationshipDatesStep";
import OnboardingKidsStep from "./components/OnboardingKidsStep";
import OnboardingContestedStep from "./components/OnboardingContestedStep";
import OnboardingFilingStatusStep from "./components/OnboardingFilingStatusStep";
import OnboardingSpouseAwarenessStep from "./components/OnboardingSpouseAwarenessStep";
import OnboardingRepresentationStep from "./components/OnboardingRepresentationStep";
import OnboardingMajorConcernsStep from "./components/OnboardingMajorConcernsStep";

import { Text } from "../../../../design/text";
import { useMatter } from "../../../../contexts/MatterContext";
import { CreateMatterForm } from "../../../../models/CreateMatterForm";

const OnboardingMatter = () => {
  const { createMatter } = useMatter();
  const [isSaving, setIsSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(parseInt(searchParams.get("step") || "0"));
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CreateMatterForm>({
    matterTypeId: "681f6c2ecf32a671f2cb4abe",
    relationshipDetails: {},
    me: { firstName: "", lastName: "", name: "", dateOfBirth: null },
    spouse: { firstName: "", lastName: "", name: "", dateOfBirth: null },
    concerns: [],
  });

  const nextStep = async () => {
    if (step === 10) {
      setIsSaving(true);
      try {
        const matterId = await createMatter(formData);
        localStorage.setItem("currentMatterId", matterId);
        navigate(`/`);
      } catch (e) {
        console.error(e);
      } finally {
        setIsSaving(false);
      }
      return;
    } else if (step === 7 && formData.divorceFilingStatus === "spouse_filed") {
      formData.spouseAwarenessStatus = "aware";
      setStep((prev) => prev + 2);
    } else {
      setStep((prev) => prev + 1);
    }
  };
  const previousStep = () => setStep((prev) => prev - 1);

  const onLogoutButtonPressed = () => {
    signOut(auth);
    navigate("/");
  };

  return (
    <>
      <div className="absolute flex items-center justify-between w-full max-w-screen-md p-4">
        <Text
          onClick={onLogoutButtonPressed}
          className="cursor-pointer text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
        >
          Logout
        </Text>
      </div>
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="w-full max-w-screen-md">
          {step === 0 && (
            <OnboardingEligibilityStep
              formData={formData}
              setFormData={setFormData}
              onBack={previousStep}
              onStepComplete={nextStep}
            />
          )}

          {step === 1 && (
            <OnboardingMeStep
              formData={formData}
              setFormData={setFormData}
              onBack={previousStep}
              onStepComplete={nextStep}
            />
          )}

          {step === 2 && (
            <OnboardingSpouseStep
              formData={formData}
              setFormData={setFormData}
              onBack={previousStep}
              onStepComplete={nextStep}
            />
          )}

          {step === 3 && (
            <OnboardingRelationshipStatusStep
              formData={formData}
              setFormData={setFormData}
              onBack={previousStep}
              onStepComplete={nextStep}
            />
          )}

          {step === 4 && (
            <OnboardingRelationshipDatesStep
              formData={formData}
              setFormData={setFormData}
              onBack={previousStep}
              onStepComplete={nextStep}
            />
          )}

          {step === 5 && (
            <OnboardingKidsStep
              formData={formData}
              setFormData={setFormData}
              onBack={previousStep}
              onStepComplete={nextStep}
            />
          )}

          {step === 6 && (
            <OnboardingContestedStep
              formData={formData}
              setFormData={setFormData}
              onBack={previousStep}
              onStepComplete={nextStep}
            />
          )}

          {step === 7 && (
            <OnboardingFilingStatusStep
              formData={formData}
              setFormData={setFormData}
              onBack={previousStep}
              onStepComplete={nextStep}
            />
          )}

          {step === 8 && (
            <OnboardingSpouseAwarenessStep
              formData={formData}
              setFormData={setFormData}
              onBack={previousStep}
              onStepComplete={nextStep}
            />
          )}

          {step === 9 && (
            <OnboardingRepresentationStep
              formData={formData}
              setFormData={setFormData}
              onBack={previousStep}
              onStepComplete={nextStep}
            />
          )}

          {step === 10 && (
            <OnboardingMajorConcernsStep
              isSaving={isSaving}
              formData={formData}
              setFormData={setFormData}
              onBack={previousStep}
              onStepComplete={nextStep}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default OnboardingMatter;
